import { useEffect, useRef, useState, Suspense } from "react";
import styled from "styled-components";
import { breakpoints } from "@/styles/sizes";
import TourInfoSection from "@/components/tour/sections/TourInfoSection";
import TourHeroSection from "@/components/tour/sections/TourHeroSection";
import dynamic from "next/dynamic";
import {useRouter} from "next/router";
import EmbeddedPageSection from "@/components/other/EmbeddedPageSection"; // Import Lazy Loader

// Dynamic imports for below-the-fold content
const TourWillDoSection = dynamic(() => import("@/components/tour/sections/TourWillDoSection"));
const TourBookingSection = dynamic(() => import("@/components/tour/sections/TourBookingSection"));
const TourMediaSection = dynamic(() => import("@/components/tour/sections/TourMediaSection"));
const TourTasteSection = dynamic(() => import("@/components/tour/sections/TourTasteSection"));
const TourMeetingSection = dynamic(() => import("@/components/tour/sections/TourMeetingSection"));
const TourWhySection = dynamic(() => import("@/components/tour/sections/TourWhySection"));
const TourSimilarSection = dynamic(() => import("@/components/tour/sections/TourSimilarSection"));
const TourFaqSection = dynamic(() => import("@/components/tour/sections/TourFaqSection"));

// Styled Components
const MeetingWhyWrapper = styled.div`
    display: grid;
    grid-template-columns: 7fr 5fr;
    gap: 40px;

    @media only screen and (${breakpoints.mobile}) {
        grid-template-columns: 1fr;
    }
`;

const WillDoBookingWrapper = styled.div`
    display: grid;
    grid-template-columns: 8fr 410px;
    gap: 40px;

    // ✅ This ensures children don’t force the grid wider than screen
    > * {
        min-width: 0;
    }

    @media only screen and (${breakpoints.mobile}) {
        grid-template-columns: 1fr;
        padding: 20px;
    }
`;


const BookingWrapper = styled.div`
    margin-top:${props => props.marginTop};
    @media only screen and (${breakpoints.mobile}) {
        margin-top:50px;
    }
`;


export default function TourPage({ data }) {
    const { reviewOverlay, page, footer, socialLinks } = data?.data || {};
    const { title, city, heroImage, isUpgrade, data: pageData, faqs, images, videoThumbnail, similarTours } = page || {};
    const { info, media, whatYouWillDo, meetings, whatYouWillTaste, why, similar, embedded } = pageData || {};

    const sidebarScrollToDivRef = useRef(null);
    const scrollToDivRef = useRef(null);
    const tourTasteRef = useRef(null);
    const titleRef = useRef();
    const infoRef = useRef();


    const router = useRouter();

    useEffect(() => {
        if (router.isReady && router.asPath.includes("#FAQ") || router.asPath.includes("#BOOKING")|| router.asPath.includes("#CONTACT")) {
            setTimeout(() => {
                if(router.asPath.includes("#FAQ")){
                    const section = document.getElementById("FAQ");
                    if (section) {
                        section.scrollIntoView({ behavior: "smooth" });
                    }
                }else if(router.asPath.includes("#BOOKING")){
                    const section = document.getElementById("BOOKING");
                    if (section) {
                        section.scrollIntoView({ behavior: "smooth" });
                    }
                }else if(router.asPath.includes("#CONTACT")){
                    const section = document.getElementById("CONTACT");
                    if (section) {
                        section.scrollIntoView({ behavior: "smooth" });
                    }
                }
            },500)
        }
    }, [router]);

    const [bookingMarginTop, setBookingMarginTop] = useState("0px");

    useEffect(() => {
        if (typeof window === 'undefined') return;

        function updateMargin() {
            const titleHeight = titleRef.current?.offsetHeight || 0;
            const infoHeight = infoRef.current?.offsetHeight || 0;
            setBookingMarginTop(`-${titleHeight + infoHeight}px`);
        }

        updateMargin();

        window.addEventListener("resize", updateMargin);
        return () => window.removeEventListener("resize", updateMargin);
    }, []);

    const [meetingMarginTop, setMeetingMarginTop] = useState("0px");

    useEffect(() => {
        if (typeof window === 'undefined') return;

        function updateTasteMargin() {
            const tourTasteRefHeight = tourTasteRef.current?.offsetHeight || 0;
            setMeetingMarginTop(`-${tourTasteRefHeight}px`);
        }

        updateTasteMargin();

        window.addEventListener("resize", updateTasteMargin);
        return () => window.removeEventListener("resize", updateTasteMargin);
    }, []);



    return (
        <>
            {/* Above-the-Fold Content */}
            <TourHeroSection
                titleRef={titleRef}
                reviewOverlayData={reviewOverlay}
                title={title}
                cityName={city?.name}
                heroMedia={heroImage}
                backgroundImageUrl={heroImage?.url}
                scrollToDivRef={sidebarScrollToDivRef}
                socialLinks={socialLinks}
                heroTitle={info?.heroTitle}
                rating={info?.rating}
                subtitle={pageData?.hero?.subtitle}
                isUpgrade={isUpgrade}
                breadcrumbds={
                    isUpgrade
                        ? [
                            { title: "Home", link: "/" },
                            { title: city?.name, link: city?.slug },
                            { title: page?.upgradeForTour?.title, link: page?.upgradeForTour?.slug },
                            { title },
                        ]
                        : [
                            { title: "Home", link: "/" },
                            { title: city?.name, link: city?.slug },
                            { title },
                        ]
                }
                dropdownData={pageData?.showDropdown ? pageData?.dropdowns : []}
                dropdownBackButtonText={page?.city?.name}
                dropdownBackButtonLink={page?.city?.slug}
                isEmbeddedPage={!!embedded?.embed}
            />

            <div ref={sidebarScrollToDivRef}/>

            {
                embedded?.embed && <EmbeddedPageSection embed={embedded?.embed} />
            }

            {
                !embedded?.embed && <>
                    <div ref={infoRef}>
                        <TourInfoSection
                            duration={info?.duration}
                            location={info?.location}
                            numOfStops={info?.numOfStops ? `${info?.numOfStops} ${info?.stopsText ?? 'stops'}` : null}
                            numOfPeople={info?.numOfPeople ? `${info?.numOfPeople} ${info?.peopleText ?? 'people max'}` : null}
                            type={info?.type}
                            funFact={info?.funFact}
                            language={info?.language}
                            wineTastings={info?.wineTastings}
                        />
                    </div>







                    {/* Below-the-Fold Content */}
                        <div className="container_small">
                            <WillDoBookingWrapper>
                                <div ref={scrollToDivRef} style={{marginTop:'40px'}}>
                                    <TourWillDoSection
                                        title={whatYouWillDo?.title}
                                        description={whatYouWillDo?.description}
                                        note={whatYouWillDo?.note}
                                        items={whatYouWillDo?.items}
                                        isUpgrade={isUpgrade}
                                        upgradeTour={page?.upgradeTour}
                                    />
                                </div>
                                <BookingWrapper
                                    marginTop={bookingMarginTop}
                                >
                                    <TourBookingSection
                                        customBookingText={pageData?.booking?.customBookingText}
                                        disabledText={pageData?.booking?.disabledText}
                                        embed={pageData?.booking?.embed}
                                        price={info?.price}
                                        crossedPrice={info?.crossedPrice}
                                        currency={info?.currency}
                                        title={pageData?.booking?.title}
                                    />
                                </BookingWrapper>

                            </WillDoBookingWrapper>
                        </div>

                        <TourMediaSection
                            images={images}
                            videoUrl={media?.videoUrl}
                            videoThumbnail={videoThumbnail}

                            videoTitle={media?.videoTitle}
                            videoSubtitle={media?.videoSubtitle}
                            imagesTitle={media?.imagesTitle}
                            imagesSubtitle={media?.imagesSubtitle}
                        />

                        <div ref={tourTasteRef}>
                            <TourTasteSection
                                title={whatYouWillTaste?.title}
                                food={whatYouWillTaste?.food}
                                drinks={whatYouWillTaste?.drinks}
                                subtext={whatYouWillTaste?.subtext}
                            />
                        </div>

                        <div className="container_small">
                            <MeetingWhyWrapper>
                                <TourWhySection
                                    style={{marginTop:'-100px'}}
                                    className="tourWhySection"
                                    title={why?.title}
                                    description={why?.description}
                                    image={page?.whyImage}
                                    footerData={footer}
                                />

                                <TourMeetingSection
                                    style={{ marginTop: meetingMarginTop }}
                                    className="tourMeetingSection"
                                    guideInfo={meetings?.guideInfo}
                                    title={meetings?.title}
                                    description={meetings?.description}

                                    email={meetings?.email}
                                    spots={meetings?.spots}
                                    isUpgrade={isUpgrade}
                                    fullyBooked={meetings?.fullyBooked}
                                    fullyBookedText={meetings?.fullBookedText}

                                    cityName={city?.name}
                                />


                            </MeetingWhyWrapper>
                        </div>

                        <TourSimilarSection
                            title={similar?.title}
                            tours={similarTours}
                            subtitle={similar?.subtitle}
                            sidebarTitle={similar?.sidebarTitle}
                            historicalTour={{
                                rating: city?.data?.historicalCard?.rating,
                                title: city?.data?.historicalCard?.title,
                                description: city?.data?.historicalCard?.description,
                                buttonText: city?.data?.historicalCard?.buttonText,
                                buttonLink: city?.data?.historicalCard?.buttonLink,
                                image: city?.historicalTourCardImage,
                            }}
                        />

                    {faqs?.length > 0 && (<TourFaqSection faqs={faqs} />)}
                </>
            }

        </>
    );
}
